import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import usePageTitle from '../hooks/usePageTitle';
import { Navbar } from '../components/Navbar';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

// Protected Route — redirects unauthenticated users to login
export const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// VBB Staff Protected Route — only accessible by @verybigbox.com emails
export const VBBProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  const email = user.email?.toLowerCase() || '';
  if (!email.endsWith('@verybigbox.com')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-center p-8">
          <div className="text-6xl mb-4">&#128274;</div>
          <h1 className="text-2xl font-bold text-white mb-2">Access Restricted</h1>
          <p className="text-slate-400 mb-4">This area is only accessible to VBB staff members.</p>
          <a href="/" className="text-blue-400 hover:text-blue-300">Return to Home</a>
        </div>
      </div>
    );
  }
  
  return children;
};

// Standard Layout with Navbar + Footer
export const Layout = ({ children, showNav = true, pageTitle = null }) => {
  usePageTitle(pageTitle);
  
  return (
    <div className="min-h-screen flex flex-col">
      {showNav && <Navbar />}
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
};

// Domain detection for multi-club routing
export const getDomainContext = () => {
  const hostname = window.location.hostname;
  
  // Preview and local environments default to AADC (the main club)
  if (
    hostname.includes('localhost') ||
    hostname.includes('preview.emergentagent.com') ||
    hostname.includes('127.0.0.1')
  ) {
    return { type: 'club', club: 'all-american-dog-club' };
  }

  // WorldPets is now a sub-club, not the default
  if (hostname === 'worldpets.club' || hostname === 'www.worldpets.club') {
    return { type: 'club', club: 'worldpets' };
  }

  if (
    hostname === 'allamericandog.club' ||
    hostname === 'www.allamericandog.club' ||
    hostname === 'all-american.pet' ||
    hostname === 'www.all-american.pet' ||
    hostname.endsWith('.all-american.pet')
  ) {
    return { type: 'club', club: 'all-american-dog-club' };
  }
  
  if (hostname === 'staging.allamericandog.club') {
    return { type: 'club', club: 'all-american-dog-club', isStaging: true };
  }

  return { type: 'custom', club: null, hostname };
};

// Hook for domain context with async lookup for custom domains
export const useDomainContext = () => {
  const [domainContext, setDomainContext] = useState(() => getDomainContext());
  const [domainLoading, setDomainLoading] = useState(domainContext.type === 'custom');

  useEffect(() => {
    const lookupDomain = async () => {
      if (domainContext.type !== 'custom' || !domainContext.hostname) {
        return;
      }

      setDomainLoading(true);
      try {
        const response = await fetch(`${API}/api/clubs/by-domain/${domainContext.hostname}`);
        if (response.ok) {
          const data = await response.json();
          
          if (data.club) {
            setDomainContext({
              type: 'club',
              club: data.club.slug,
              clubName: data.club.name,
              isStaging: data.is_staging
            });
          } else {
            // Fallback to AADC as the default club
            setDomainContext({ type: 'club', club: 'all-american-dog-club' });
          }
        } else {
          // Fallback to AADC as the default club
          setDomainContext({ type: 'club', club: 'all-american-dog-club' });
        }
      } catch (error) {
        console.error('Domain lookup failed:', error);
        // Fallback to AADC as the default club
        setDomainContext({ type: 'club', club: 'all-american-dog-club' });
      } finally {
        setDomainLoading(false);
      }
    };

    lookupDomain();
  }, [domainContext.type, domainContext.hostname]);

  return { domainContext, domainLoading };
};
